import type { PackBundle } from './types';

// Verify the HMAC-SHA256 signature on a pack bundle. The signing layout
// matches scripts/build-pack.ts:sign — `JSON.stringify(bundle minus signature)`
// hashed with `PACK_SIGN_KEY` and rendered as a lowercase hex string.
//
// Returns true on a match, false on mismatch. Throws only if the crypto
// primitive is unavailable (very old environments).
export async function verifyBundleSignature(
  bundle: PackBundle,
  signKey: string,
): Promise<boolean> {
  if (typeof crypto?.subtle?.importKey !== 'function') {
    throw new Error('SubtleCrypto unavailable — cannot verify pack signature');
  }
  // Reconstruct the unsigned payload exactly as the signer saw it.
  const { signature, ...unsigned } = bundle;
  const payload = JSON.stringify(unsigned);

  const key = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(signKey),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign', 'verify'],
  );
  const macBytes = await crypto.subtle.sign(
    'HMAC',
    key,
    new TextEncoder().encode(payload),
  );
  const expected = Array.from(new Uint8Array(macBytes))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return constantTimeEqual(expected, signature);
}

function constantTimeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}
